import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useShallow } from "zustand/react/shallow";
import { ModelElementType } from "@/components/entities";
import { FetchListOptions } from "@/components/main/stores/fetchWithInit";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { useAppInitializationStore } from "@/components/main/stores/appInitializationStore";
import { useOnlineStatus } from "@/components/hooks/useOnlineStatus";
import {
  pollingAllowed,
  useResumeRefresh,
} from "@/components/hooks/activityGate";
import { showNotificationGlobal } from "@/stores/useNotificationStore";

// Ambient cadence only — mutations and resume (focus/idle-wake) refetch
// immediately, so this is just the "someone shared with me" catch-up.
const POLL_INTERVAL_MS = 30_000;

export interface ModelsPolling {
  isLoading: boolean;
  isOnline: boolean;
  isAppInitialized: boolean;
  modelsInitialized: boolean;
  updateModelList: (
    options?: FetchListOptions,
  ) => Promise<ModelElementType[] | void>;
}

/** Init-fetch + gated ambient polling + resume/online-restore refetch for the
 *  models list. Extracted from ModelsPage so the Overview page shares one
 *  behavior. */
export const useModelsPolling = (): ModelsPolling => {
  const isOnline = useOnlineStatus();
  const intervalRef = useRef<number | null>(null);
  const initializedRef = useRef(false);

  const { isLoading, fetchModelsList } = useMainStateStore(
    useShallow((state) => ({
      isLoading: state.isLoading,
      fetchModelsList: state.fetchModelsList,
    })),
  );
  const { isAppInitialized, modelsInitialized } = useAppInitializationStore(
    useShallow((state) => ({
      isAppInitialized: state.isAppInitialized(),
      modelsInitialized: state.modelsInitialized,
    })),
  );

  const updateModelList = useCallback(
    async (options?: FetchListOptions) => {
      return await fetchModelsList(options);
    },
    [fetchModelsList],
  );

  useEffect(() => {
    if (!initializedRef.current && navigator.onLine) {
      initializedRef.current = true;
      // Without `refresh` this is a cache hit once the store is initialized,
      // so arriving at a list page fetched nothing and waited for the tick.
      void updateModelList({
        showLoading: !isAppInitialized && !modelsInitialized,
        refresh: true,
      });
    }

    intervalRef.current = window.setInterval(() => {
      if (!navigator.onLine || !pollingAllowed()) return;
      void updateModelList({ showLoading: false, refresh: true });
    }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [updateModelList, modelsInitialized, isAppInitialized]);

  useResumeRefresh(
    useCallback(() => {
      if (navigator.onLine) {
        void updateModelList({ showLoading: false, refresh: true });
      }
    }, [updateModelList]),
  );

  // Immediate fetch when online restores
  const prevOnlineRef = useRef(isOnline);
  useEffect(() => {
    const wasOnline = prevOnlineRef.current;
    prevOnlineRef.current = isOnline;
    if (isOnline && !wasOnline) {
      void updateModelList({ showLoading: false, refresh: true });
    }
  }, [isOnline, updateModelList]);

  return {
    isLoading,
    isOnline,
    isAppInitialized,
    modelsInitialized,
    updateModelList,
  };
};

/** Shows the duplicate-session notice carried via location.state after a
 *  canvas redirect, then clears it from history. */
export const useDuplicateSessionNotice = (): void => {
  const location = useLocation();
  useEffect(() => {
    const state = location.state as { duplicateSession?: boolean } | null;
    if (state?.duplicateSession) {
      showNotificationGlobal(
        "This model is already active in a different tab or window!",
      );
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
};
